import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCheck, CircleAlert, RefreshCw, RotateCcw, ServerCrash, Trash2 } from 'lucide-react';
import { api } from '@/services/api';
import { DataTable } from '@/components/tables/data-table';
import { PageHeader } from '@/components/ui/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Drawer } from '@/components/ui/drawer';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast';

function tone(value) {
  if (value === 'critical' || value === 'resolved') return value === 'critical' ? 'error' : 'success';
  if (value === 'warning' || value === 'open') return 'warning';
  if (value === 'acknowledged') return 'accent';
  return 'neutral';
}

function formatTime(value) {
  return value ? new Date(value).toLocaleString() : '—';
}

export function BackendErrorCenterPage() {
  const [state, setState] = useState('');
  const [severity, setSeverity] = useState('');
  const [selected, setSelected] = useState();
  const [resolutionNote, setResolutionNote] = useState('');
  const client = useQueryClient();
  const toast = useToast();
  const query = useQuery({
    queryKey: ['backend-error-center', state, severity],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '200' });
      if (state) params.set('state', state);
      if (severity) params.set('severity', severity);
      return api(`/api/v1/admin/backend-errors?${params}`);
    },
    refetchInterval: 15_000,
  });
  const rows = query.data?.data ?? [];
  const summary = query.data?.summary ?? {};
  const update = useMutation({
    mutationFn: ({ id, nextState }) => api(`/api/v1/admin/backend-errors/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ state: nextState, resolutionNote: resolutionNote.trim() || undefined }),
    }),
    onSuccess: async (result) => {
      setSelected(result.data);
      await client.invalidateQueries({ queryKey: ['backend-error-center'] });
      toast.show('Error status updated', 'success');
    },
    onError: (cause) => toast.show(cause.message, 'error', 'Update failed'),
  });
  const clearResolved = useMutation({
    mutationFn: () => api('/api/v1/admin/backend-errors/resolved', { method: 'DELETE' }),
    onSuccess: async (result) => {
      await client.invalidateQueries({ queryKey: ['backend-error-center'] });
      toast.show(`${result.data?.deleted ?? 0} resolved errors removed`, 'success');
    },
    onError: (cause) => toast.show(cause.message, 'error', 'Cleanup failed'),
  });
  const columns = useMemo(() => [
    { key: 'severity', header: 'Severity', width: 110, cell: (row) => <Badge tone={tone(row.severity)}>{row.severity}</Badge> },
    { key: 'code', header: 'Code', width: 210, cell: (row) => <div><strong className="text-xs text-slate-200">{row.code}</strong><small className="mt-1 block font-mono text-[9px] text-slate-600">{row.requestId || 'no request id'}</small></div> },
    { key: 'request', header: 'Request', width: 280, searchValue: (row) => `${row.method} ${row.path}`, cell: (row) => <div><strong className="text-xs text-cyan-200">{row.method} {row.statusCode}</strong><small className="mt-1 block truncate text-[10px] text-slate-500">{row.path}</small></div> },
    { key: 'message', header: 'Message', width: 360, cell: (row) => <span className="line-clamp-2 text-xs text-slate-300">{row.message}</span> },
    { key: 'occurrenceCount', header: 'Count', width: 90, cell: (row) => <span className="font-mono text-xs font-bold text-slate-200">{row.occurrenceCount}</span> },
    { key: 'state', header: 'State', width: 130, cell: (row) => <Badge tone={tone(row.state)}>{row.state}</Badge> },
    { key: 'lastOccurredAt', header: 'Last seen', width: 190, cell: (row) => <span className="text-[10px] text-slate-500">{formatTime(row.lastOccurredAt)}</span> },
  ], []);

  return <div className="workspace-page">
    <PageHeader
      eyebrow="Backend observability"
      title="Error Center"
      description="Review backend failures and close resolved issues."
      meta={<div className="flex flex-wrap gap-2"><Badge tone="warning">{summary.open?.count ?? 0} open</Badge><Badge tone="accent">{summary.acknowledged?.count ?? 0} acknowledged</Badge><Badge tone="success">{summary.resolved?.count ?? 0} resolved</Badge></div>}
      actions={<div className="flex gap-2"><Button variant="secondary" onClick={() => query.refetch()}><RefreshCw size={14}/>Refresh</Button><Button variant="danger" onClick={() => clearResolved.mutate()} disabled={clearResolved.isPending}><Trash2 size={14}/>Clear resolved</Button></div>}
    />
    <div className="workspace-fill overflow-hidden border border-white/8 bg-[#07101a]">
      <DataTable
        tableId="backend-error-center"
        rows={rows}
        columns={columns}
        getKey={(row) => row.id || row._id}
        onRowClick={(row) => { setSelected(row); setResolutionNote(row.resolutionNote || ''); }}
        onRefresh={() => query.refetch()}
        loading={query.isFetching}
        searchPlaceholder="Search request ID, code, endpoint, or message"
        filters={[
          { key: 'state', label: 'State', value: state, onClear: () => setState(''), render: <Select value={state} onChange={(event) => setState(event.target.value)}><option value="">All states</option><option value="open">Open</option><option value="acknowledged">Acknowledged</option><option value="resolved">Resolved</option></Select> },
          { key: 'severity', label: 'Severity', value: severity, onClear: () => setSeverity(''), render: <Select value={severity} onChange={(event) => setSeverity(event.target.value)}><option value="">All severity</option><option value="critical">Critical</option><option value="warning">Warning</option><option value="info">Info</option></Select> },
        ]}
        bulkActions={[
          { label: 'Acknowledge', icon: <CheckCheck size={13}/>, onClick: async (items, clear) => { await Promise.all(items.map((item) => api(`/api/v1/admin/backend-errors/${item.id || item._id}`, { method: 'PATCH', body: JSON.stringify({ state: 'acknowledged' }) }))); clear(); await client.invalidateQueries({ queryKey: ['backend-error-center'] }); } },
          { label: 'Resolve', icon: <CircleAlert size={13}/>, onClick: async (items, clear) => { await Promise.all(items.map((item) => api(`/api/v1/admin/backend-errors/${item.id || item._id}`, { method: 'PATCH', body: JSON.stringify({ state: 'resolved' }) }))); clear(); await client.invalidateQueries({ queryKey: ['backend-error-center'] }); } },
        ]}
      />
    </div>
    <Drawer open={Boolean(selected)} onClose={() => setSelected(undefined)} eyebrow="Backend error" title={selected?.code || 'Error detail'} width="max-w-2xl">
      {selected && <div className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-3"><Info icon={ServerCrash} label="Status" value={`${selected.statusCode} · ${selected.severity}`}/><Info icon={AlertTriangle} label="Occurrences" value={selected.occurrenceCount}/><Info icon={CircleAlert} label="State" value={selected.state}/></div>
        <Panel label="Request"><p className="font-mono text-xs text-cyan-200">{selected.method} {selected.path}</p><p className="mt-2 font-mono text-[10px] text-slate-600">Request ID: {selected.requestId || '—'}</p></Panel>
        <Panel label="Message"><p className="text-sm text-slate-200">{selected.message}</p></Panel>
        {selected.details && <Panel label="Safe details"><pre className="whitespace-pre-wrap break-words text-[10px] text-slate-400">{JSON.stringify(selected.details, null, 2)}</pre></Panel>}
        {selected.stack && <Panel label="Stack trace"><pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] leading-relaxed text-slate-500">{selected.stack}</pre></Panel>}
        <div><p className="mb-2 text-[9px] font-black uppercase tracking-[.16em] text-slate-600">Resolution note</p><Textarea value={resolutionNote} onChange={(event) => setResolutionNote(event.target.value)} placeholder="What fixed this issue?"/></div>
        <div className="flex flex-wrap gap-2"><Button variant="secondary" onClick={() => update.mutate({ id: selected.id || selected._id, nextState: 'open' })}><RotateCcw size={14}/>Reopen</Button><Button variant="secondary" onClick={() => update.mutate({ id: selected.id || selected._id, nextState: 'acknowledged' })}><CheckCheck size={14}/>Acknowledge</Button><Button onClick={() => update.mutate({ id: selected.id || selected._id, nextState: 'resolved' })}><CircleAlert size={14}/>Resolve</Button></div>
      </div>}
    </Drawer>
  </div>;
}

function Info({ icon: Icon, label, value }) {
  return <div className="border border-white/8 bg-black/20 p-3"><Icon size={14} className="text-cyan-300"/><p className="mt-3 text-[8px] font-black uppercase tracking-[.14em] text-slate-600">{label}</p><p className="mt-1 text-xs font-bold capitalize text-slate-200">{value ?? '—'}</p></div>;
}
function Panel({ label, children }) {
  return <section className="border border-white/8 bg-black/15 p-4"><p className="mb-3 text-[9px] font-black uppercase tracking-[.16em] text-slate-600">{label}</p>{children}</section>;
}
